import { useState } from 'react';
import {
  ChevronDown,
  ChefHat,
  Sofa,
  Bed,
  Bath,
  CircleCheck,
  Check,
  Image,
  type LucideIcon,
} from 'lucide-react';
import { ChecklistItem } from './ChecklistItem';
import { PhotoStrip } from './PhotoStrip';
import { PhotoViewer } from './PhotoViewer';
import { VideoRecordModal } from './VideoRecordModal';
import styles from './RoomSection.module.css';

const iconMap: Record<string, LucideIcon> = {
  ChefHat,
  Sofa,
  Bed,
  Bath,
  CheckCircle: CircleCheck,
};

interface ChecklistItemType {
  id: string;
  task: string;
  completed: boolean;
  type?: 'task' | 'video';
}

interface RoomPhoto {
  id: string;
  url: string;
  caption?: string;
}

interface RoomSectionProps {
  id: string;
  name: string;
  icon: string;
  items: ChecklistItemType[];
  photos?: RoomPhoto[];
  onToggleItem: (itemId: string) => void;
  defaultExpanded?: boolean;
  cleaningId?: string;
}

export function RoomSection({
  id,
  name,
  icon,
  items,
  photos = [],
  onToggleItem,
  defaultExpanded = false,
  cleaningId,
}: RoomSectionProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [viewerVisible, setViewerVisible] = useState(false);
  const [viewerInitialIndex, setViewerInitialIndex] = useState(0);
  const [videoModalItem, setVideoModalItem] = useState<ChecklistItemType | null>(null);

  const completedCount = items.filter((item) => item.completed).length;
  const totalCount = items.length;
  const isComplete = completedCount === totalCount && totalCount > 0;
  const hasPhotos = photos.length > 0;

  const handlePhotoPress = (index: number) => {
    setViewerInitialIndex(index);
    setViewerVisible(true);
  };

  const handleItemPress = (item: ChecklistItemType) => {
    if (item.type === 'video' && !item.completed) {
      setVideoModalItem(item);
    } else {
      onToggleItem(item.id);
    }
  };

  const handleVideoComplete = () => {
    if (videoModalItem) {
      onToggleItem(videoModalItem.id);
      setVideoModalItem(null);
    }
  };

  const IconComponent = iconMap[icon] || CircleCheck;
  const firstPhoto = hasPhotos ? photos[0] : null;

  return (
    <div className={styles.container}>
      <button
        className={styles.header}
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
      >
        <div className={`${styles.iconContainer} ${isComplete ? styles.iconContainerComplete : ''}`}>
          {firstPhoto ? (
            <>
              <img src={firstPhoto.url} alt="" className={styles.iconImage} />
              {isComplete && (
                <div className={styles.completeOverlay}>
                  <Check size={18} strokeWidth={3} />
                </div>
              )}
            </>
          ) : (
            <IconComponent size={20} />
          )}
        </div>
        <div className={styles.headerContent}>
          <div className={styles.nameRow}>
            <span className={styles.name}>{name}</span>
          </div>
          <div className={styles.countRow}>
            <span className={`${styles.count} ${isComplete ? styles.countComplete : ''}`}>
              {completedCount}/{totalCount} tasks
            </span>
            {hasPhotos && (
              <span className={styles.photoCount}>
                <Image size={12} />
                {photos.length}
              </span>
            )}
          </div>
        </div>
        <ChevronDown
          size={20}
          className={`${styles.chevron} ${expanded ? styles.chevronExpanded : ''}`}
        />
      </button>

      {expanded && (
        <>
          {hasPhotos && <PhotoStrip photos={photos} onPhotoPress={handlePhotoPress} />}
          <div className={styles.itemsContainer}>
            {items.map((item) => (
              <ChecklistItem
                key={item.id}
                task={item.task}
                completed={item.completed}
                type={item.type}
                onToggle={() => handleItemPress(item)}
              />
            ))}
          </div>
        </>
      )}

      <PhotoViewer
        photos={photos}
        initialIndex={viewerInitialIndex}
        visible={viewerVisible}
        onClose={() => setViewerVisible(false)}
      />

      <VideoRecordModal
        visible={!!videoModalItem}
        roomName={name}
        onClose={() => setVideoModalItem(null)}
        onComplete={handleVideoComplete}
        cleaningId={cleaningId}
        roomId={id}
      />
    </div>
  );
}

